import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import type { Group as GroupT, Question as QuestionT } from "../types";
import Question from "./Question";
import { stripHtml } from "../util/text";
import { isHidden } from "../util/rule";
import { useAnswers } from "../state/AnswersContext";
import CustomerPartNumberField from "./CustomerPartNumberField";
import AuxColumnControl from "./AuxColumnControl";
import { isFah, isFahSectionGroupCode, fahSectionHideState } from "../util/fahSections";
import { isManuallyHidden } from "../util/hiddenQuestions";
import { isCollapsedSection, isConfirmed, openSection, sectionModeOn, useSections } from "../util/sectionMode";

type Props = {
  group: GroupT;
  questions: QuestionT[];
  /** Shown but not required on this launch — a quote outside Customer Requirements and the
   *  burner internals. The heading gets "(optional)"; validation skips the same set, so the
   *  label and the submit rule cannot disagree. */
  optional?: boolean;
  /** Section mode (util/sectionMode.ts): this group is a section under review / to confirm;
   *  `sectionNext` is the section after it on the tab, opened by "Next section". */
  section?: boolean;
  sectionNext?: string | null;
};

// The list of manually hidden questions moved to util/hiddenQuestions.ts so
// validation.ts reads the SAME set. Hiding a question here while validation still
// required it produced a Save & Submit that blocked on an invisible field; that is
// no longer possible to get wrong in one place only.

export default function Group({ group, questions, optional = false, section = false, sectionNext = null }: Props) {
  const { answers, invalidFields, product } = useAnswers();
  // Section mode re-renders on open / confirm (the store's seq).
  useSections();
  const sectionMode = section && sectionModeOn();
  const sectionCollapsed = sectionMode && isCollapsedSection(group.id);
  const sectionConfirmed = sectionMode && isConfirmed(group.id);
  const groupCode = group.custrecord_cpqc_group_code;
  const collapsable = group.custrecord_cpqc_group_collapsable;

  // FAH: reveal the Main Burner / Pilot / Ignitor sections collapsed by default
  // (see fahSections.ts). Other groups keep their own collapsed default.
  const fahSection = isFahSectionGroupCode(product, groupCode);
  const fah = isFah(answers);
  const defaultCollapsed =
    group.custrecord_cpqc_group_collapsed_default || (fahSection && fah);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  // Re-apply the FAH-aware default when the FAH state itself flips: choosing
  // "0 - FAH" collapses these sections; leaving FAH restores their normal
  // default. A manual toggle in between is preserved until the next flip.
  const prevFahRef = useRef(fah);
  useEffect(() => {
    if (fahSection && prevFahRef.current !== fah) {
      setCollapsed(group.custrecord_cpqc_group_collapsed_default || fah);
    }
    prevFahRef.current = fah;
  }, [fah, fahSection, group.custrecord_cpqc_group_collapsed_default]);

  const label = stripHtml(group.custrecord_cpqc_group_label_html) || group.name;
  const showBox = group.custrecord_cpqc_group_show_box;

  // For an FAH's three sections, neutralise the "no main burner" signal so their
  // fields (e.g. Pilot Type) render; identical to `answers` for every other case.
  const qHideState = useMemo(
    () => fahSectionHideState(groupCode, answers),
    [groupCode, answers]
  );

  // From-Center mode reveals two inlet rows that NetSuite otherwise hides unless
  // an inlet is Firetube: the From Center (Left/Right) table and the IN row
  // (which becomes "Direction 1 / Reference to Center"). We force-show them here
  // rather than editing the firetube-gated hide rules (faithful port) — same
  // philosophy as MANUALLY_HIDDEN_QUESTION_CODES, just inverted.
  const fromCenterMode = answers["FROM_CENTER_MODE/ON"] === true;

  // NOTE: deliberately do NOT consult custrecord_cpqc_question_hide here.
  // In this export only CALCULATE2 (the button) and the 2 SPACERs have it
  // set to true — none of those should be filtered out.
  const visibleQuestions = useMemo(
    () =>
      questions.filter((q) => {
        const qc = q.custrecord_cpqc_question_code;
        if (isManuallyHidden(product, qc)) return false;
        if (fromCenterMode && (qc === "IN" || qc === "FROM_CENTER")) return true;
        return !isHidden(
          {
            hideRule: q.custrecord_cpqc_question_hide_rule,
            parsedHideRule: q.custrecord_cpqc_question_p_hide_rule,
          },
          qHideState
        );
      }),
    [questions, qHideState, fromCenterMode, product]
  );

  // Never keep a group collapsed while it holds a field Save & Submit flagged —
  // otherwise the field isn't rendered and its red highlight can't show (and the
  // auto-scroll would have nothing to land on).
  const hasInvalid = visibleQuestions.some((q) =>
    invalidFields.has(q.custrecord_cpqc_question_code)
  );
  const effectiveCollapsed = (sectionMode ? sectionCollapsed : collapsed) && !hasInvalid;

  return (
    <div
      className={`cpq-group ${showBox ? "has-box" : ""}${sectionConfirmed ? " is-confirmed" : ""}${sectionCollapsed ? " is-collapsed-section" : ""}`}
      data-code={group.custrecord_cpqc_group_code}
    >
      <header
        className="cpq-group-header"
        onClick={sectionMode ? (sectionCollapsed ? () => openSection(group.id) : undefined) : collapsable ? () => setCollapsed((c) => !c) : undefined}
        style={{ cursor: sectionMode ? (sectionCollapsed ? "pointer" : "default") : collapsable ? "pointer" : "default" }}
      >
        <h2 className="cpq-group-title">
          {label}
          {optional && <span className="cpq-group-optional"> (optional)</span>}
        </h2>
        {sectionMode ? (
          <span className="cpq-group-toggle" title={sectionConfirmed ? "Reviewed" : sectionCollapsed ? "Open to review" : "Under review"}>
            {sectionConfirmed ? "✓" : sectionCollapsed ? "+" : "−"}
          </span>
        ) : collapsable ? (
          <span className="cpq-group-toggle">{effectiveCollapsed ? "+" : "−"}</span>
        ) : null}
      </header>
      {!effectiveCollapsed && (
        <div className="cpq-group-body">
          {/* Coupling Inlet Connections: add/remove Aux columns across all the
              inlet tables at once. */}
          {group.custrecord_cpqc_group_code === "INLET_CONNECTIONS_GROUP" && (
            <AuxColumnControl />
          )}
          {visibleQuestions.length === 0 ? (
            <p className="cpq-empty-small">No questions in this group yet.</p>
          ) : (
            visibleQuestions.map((q) => (
              <Fragment key={q.id}>
                <Question question={q} />
                {/* Web-app-only field placed immediately to the right of the
                    Order Type ("New Build") dropdown. (The Item Template
                    shortcut moved to the page header — see Configurator.) */}
                {q.custrecord_cpqc_question_code === "ORDER_TYPE" && (
                  <CustomerPartNumberField />
                )}
              </Fragment>
            ))
          )}
          {sectionMode && !sectionConfirmed && sectionNext && (
            <div className="cpq-section-next">
              <button type="button" className="cpq-btn cpq-btn-primary" onClick={() => openSection(sectionNext)}>
                Next section
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
